/**
 * GEO-research SSE pipeline → store subscriber.
 *
 * Translates `geo:*` typed events into department-store mutations.
 * Migrated in Phase 8 commit 73.
 *
 * Local closure state captures the throttling/dedup logic that used
 * to live in the pipeline runner closure (`citationStartLogged`,
 * `lastCitationProgressLogged`, `citationWarnedQueries`). Subscribers
 * reset these flags via `geo:session-named` (the first event of any
 * pipeline run) so a subsequent run starts clean.
 */

import { sseEvents } from "./sseEvents";
import type {
  CampaignData,
  CoAgentMessage,
  GeoPipelineInput,
  StepStatus,
} from "../store/department/types";

interface GeoSubscriberHooks {
  addStepLog: (stepIndex: number, text: string) => void;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  addMessage: (msg: Omit<CoAgentMessage, "id" | "timestamp">) => void;
  setStepState: (
    updater: (state: {
      currentStepIndex: number;
      stepStatuses: Record<number, StepStatus>;
    }) => Partial<{ currentStepIndex: number; stepStatuses: Record<number, StepStatus> }>,
  ) => void;
  setCampaignName: (name: string) => void;
  setFocusedStep: (index: number) => void;
  /** Returns the current `geoInput` from `campaignData` so the subscriber
   *  can merge `resolvedTargetQueries` into the live input shape. */
  getGeoInput: () => GeoPipelineInput | undefined;
}

let wired = false;

export function wireGeoSubscriber(getHooks: () => GeoSubscriberHooks): void {
  if (wired) return;
  wired = true;

  // Throttling state — reset on every new run via `geo:session-named`.
  let citationStartLogged = false;
  let lastCitationProgressLogged = -1;
  const citationWarnedQueries = new Set<string>();
  const resetThrottles = () => {
    citationStartLogged = false;
    lastCitationProgressLogged = -1;
    citationWarnedQueries.clear();
  };

  sseEvents.subscribe("geo:session-named", ({ name }) => {
    resetThrottles();
    getHooks().setCampaignName(name);
  });

  sseEvents.subscribe("geo:site-scan-running", ({ url }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      geoResearchPhase: "site_scan",
      geoResearchProgress: { phase: "site_scan", completed: 0, total: 6 },
    });
    hooks.addStepLog(1, `Scanning site: ${url}...`);
  });

  sseEvents.subscribe("geo:site-scan-complete", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      siteScan: data as CampaignData["siteScan"],
      geoResearchPhase: "citation_baseline",
      geoResearchProgress: { phase: "site_scan", completed: 1, total: 6 },
    });
    const schemaStatus =
      (data.schema as { status?: string } | undefined)?.status ?? "unknown";
    const headingCount = Array.isArray(data.headingHierarchy)
      ? data.headingHierarchy.length
      : 0;
    hooks.addStepLog(
      1,
      `[SUCCESS] Site scan complete — schema: ${schemaStatus}, ${headingCount} headings`,
    );
    hooks.addMessage({
      role: "agent",
      content: `Site scan is ready: schema status is ${schemaStatus} with ${headingCount} headings detected. Timeline step 1 is now running AI citation checks.`,
    });
  });

  sseEvents.subscribe(
    "geo:citation-baseline-running",
    ({ data, progress, resolvedTargetQueries }) => {
      const hooks = getHooks();
      const liveGeoInput = hooks.getGeoInput();
      const updatedGeoInput =
        resolvedTargetQueries && liveGeoInput
          ? { ...liveGeoInput, targetQueries: resolvedTargetQueries }
          : liveGeoInput;
      const querySource = data.querySource === "provided" ? "provided" : "auto-derived";
      hooks.updateCampaignData({
        ...(updatedGeoInput ? { geoInput: updatedGeoInput } : {}),
        geoResearchPhase: "citation_baseline",
        geoResearchProgress: { phase: "citation_baseline", completed: 1, total: 6 },
      });
      if (!citationStartLogged) {
        citationStartLogged = true;
        hooks.addStepLog(
          1,
          `Querying AI engines for ${data.queryCount ?? 0} ${querySource} target queries...`,
        );
      }

      const progressTotal = Number(progress.total ?? 0);
      const progressCompleted = Number(progress.completed ?? 0);
      if (progressTotal > 0 && progressCompleted !== lastCitationProgressLogged) {
        const interval = Math.max(1, Math.ceil(progressTotal / 5));
        const shouldLogProgress =
          progressCompleted === 1 ||
          progressCompleted === progressTotal ||
          progressCompleted % interval === 0;
        if (shouldLogProgress) {
          const currentQuery =
            typeof data.currentQuery === "string" ? data.currentQuery : undefined;
          hooks.addStepLog(
            1,
            `[INFO] Citation checks ${progressCompleted}/${progressTotal}${
              currentQuery ? ` — ${currentQuery}` : ""
            }`,
          );
        }
        lastCitationProgressLogged = progressCompleted;
      }

      const queryErrors = Number(data.queryErrors ?? 0);
      const currentQuery = typeof data.currentQuery === "string" ? data.currentQuery : "";
      if (queryErrors > 0 && currentQuery && !citationWarnedQueries.has(currentQuery)) {
        citationWarnedQueries.add(currentQuery);
        hooks.addStepLog(
          1,
          `[WARN] ${queryErrors} engine checks failed for query: ${currentQuery}`,
        );
      }
    },
  );

  sseEvents.subscribe(
    "geo:citation-baseline-complete",
    ({ data, resolvedTargetQueries }) => {
      const hooks = getHooks();
      const liveGeoInput = hooks.getGeoInput();
      const updatedGeoInput =
        resolvedTargetQueries && liveGeoInput
          ? { ...liveGeoInput, targetQueries: resolvedTargetQueries }
          : liveGeoInput;
      hooks.updateCampaignData({
        ...(updatedGeoInput ? { geoInput: updatedGeoInput } : {}),
        aiCitations: (data.citations ?? []) as CampaignData["aiCitations"],
        aiShareOfVoice: Number(data.aiShareOfVoice ?? 0),
        geoResearchPhase: "gap_analysis",
        geoResearchProgress: { phase: "citation_baseline", completed: 2, total: 6 },
      });
      hooks.addStepLog(
        1,
        `[SUCCESS] Citation baseline: AI Share of Voice = ${data.aiShareOfVoice ?? 0}%`,
      );
      const collectionErrors = Array.isArray(data.collectionErrors)
        ? data.collectionErrors
        : [];
      if (collectionErrors.length > 0) {
        const errorEngines = [
          ...new Set(
            collectionErrors
              .map((entry) => (entry as { engine?: string } | null)?.engine)
              .filter((v): v is string => Boolean(v)),
          ),
        ];
        hooks.addStepLog(
          1,
          `[WARN] ${collectionErrors.length} citation checks failed (${
            errorEngines.join(", ") || "unknown engines"
          })`,
        );
      }
      const citationCount = Array.isArray(data.citations) ? data.citations.length : 0;
      hooks.addMessage({
        role: "agent",
        content: `Citation baseline is ready: AI Share of Voice is ${
          data.aiShareOfVoice ?? 0
        }% across ${citationCount} checks. I am now scoring gaps in timeline step 2.`,
      });
    },
  );

  sseEvents.subscribe("geo:gap-analysis-running", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      geoResearchPhase: "gap_analysis",
      geoResearchProgress: { phase: "gap_analysis", completed: 2, total: 6 },
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 2,
      stepStatuses: { ...state.stepStatuses, 1: "completed", 2: "running" },
    }));
  });

  sseEvents.subscribe("geo:gap-analysis-complete", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      geoScoreResult: data.geoScore as CampaignData["geoScoreResult"],
      contentGaps: (data.contentGaps ?? []) as CampaignData["contentGaps"],
      geoTopCompetitors: (data.topCompetitors ?? []) as CampaignData["geoTopCompetitors"],
      geoResearchPhase: "asset_generation",
      geoResearchProgress: { phase: "gap_analysis", completed: 3, total: 6 },
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 2,
      stepStatuses: { ...state.stepStatuses, 1: "completed", 2: "waiting" },
    }));
    hooks.setFocusedStep(2);
    const overallScore = (data.geoScore as { overall?: number } | undefined)?.overall ?? 0;
    const gapCount = Array.isArray(data.contentGaps) ? data.contentGaps.length : 0;
    hooks.addStepLog(
      1,
      `[SUCCESS] Gap analysis: overall score ${overallScore}/100, ${gapCount} gaps found`,
    );
    hooks.addMessage({
      role: "agent",
      content: `Gap analysis is ready. GEO score is ${overallScore}/100 with ${gapCount} prioritized issues. In the scorecard widget, review the 4 score bars, severity-ranked issues, and competitor citations.`,
      widget: "geoScorecard",
    });
  });

  sseEvents.subscribe("geo:asset-generation-running", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      geoResearchPhase: "asset_generation",
      geoResearchProgress: { phase: "asset_generation", completed: 3, total: 6 },
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 3,
      stepStatuses: { ...state.stepStatuses, 2: "completed", 3: "running" },
    }));
    hooks.addStepLog(3, "Generating JSON-LD schemas and high-information copy rewrites...");
  });

  sseEvents.subscribe("geo:asset-generation-complete", ({ data }) => {
    const hooks = getHooks();
    const jsonLdCount = Array.isArray(data.jsonLdAssets) ? data.jsonLdAssets.length : 0;
    const copyCount = Array.isArray(data.copyUpgrades) ? data.copyUpgrades.length : 0;
    hooks.updateCampaignData({
      jsonLdAssets: (data.jsonLdAssets ?? []) as CampaignData["jsonLdAssets"],
      copyUpgrades: (data.copyUpgrades ?? []) as CampaignData["copyUpgrades"],
      geoResearchPhase: "entity_authority",
      geoResearchProgress: { phase: "asset_generation", completed: 4, total: 6 },
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 3,
      stepStatuses: { ...state.stepStatuses, 3: "waiting" },
    }));
    hooks.setFocusedStep(3);
    hooks.addStepLog(
      3,
      `[SUCCESS] Generated ${jsonLdCount} schemas and ${copyCount} copy upgrades`,
    );
    hooks.addMessage({
      role: "agent",
      content: `Optimization assets are ready: ${jsonLdCount} schema blocks and ${copyCount} copy rewrites. In Asset Review, use the Schemas and Copy rewrites tabs to approve items.`,
      widget: "geoAssetReview",
    });
  });

  sseEvents.subscribe("geo:entity-authority-running", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      geoResearchPhase: "entity_authority",
      geoResearchProgress: { phase: "entity_authority", completed: 4, total: 6 },
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 4,
      stepStatuses: { ...state.stepStatuses, 3: "completed", 4: "running" },
    }));
    hooks.addStepLog(4, "Building cross-platform brand entity kit...");
  });

  sseEvents.subscribe("geo:entity-authority-complete", ({ data }) => {
    const hooks = getHooks();
    const entityPayload = data.entityPayload as
      | { distributionTargets?: unknown[] }
      | undefined;
    const targetCount = Array.isArray(entityPayload?.distributionTargets)
      ? entityPayload.distributionTargets.length
      : 0;
    hooks.updateCampaignData({
      entityPayload: data.entityPayload as CampaignData["entityPayload"],
      geoResearchPhase: "implementation_plan",
      geoResearchProgress: { phase: "entity_authority", completed: 5, total: 6 },
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 4,
      stepStatuses: { ...state.stepStatuses, 4: "waiting" },
    }));
    hooks.setFocusedStep(4);
    hooks.addStepLog(4, `[SUCCESS] Entity authority kit ready (${targetCount} platforms)`);
    hooks.addMessage({
      role: "agent",
      content: `Entity authority kit is ready with ${targetCount} platform targets. In the checklist widget you now have bio, key data points, and copy-ready distribution cards.`,
      widget: "geoEntityChecklist",
    });
  });

  sseEvents.subscribe("geo:implementation-plan-running", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      geoResearchPhase: "implementation_plan",
      geoResearchProgress: { phase: "implementation_plan", completed: 5, total: 6 },
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 5,
      stepStatuses: { ...state.stepStatuses, 4: "completed", 5: "running" },
    }));
    hooks.addStepLog(
      5,
      "Building implementation queue, file-ready artifacts, and CMS playbooks...",
    );
  });

  sseEvents.subscribe("geo:implementation-plan-complete", ({ data }) => {
    const hooks = getHooks();
    const pack = data.implementationPack as
      | { tasks?: unknown[]; quickWins?: unknown[] }
      | undefined;
    const taskCount = Array.isArray(pack?.tasks) ? pack.tasks.length : 0;
    const quickWinCount = Array.isArray(pack?.quickWins) ? pack.quickWins.length : 0;
    hooks.updateCampaignData({
      geoImplementationPack: data.implementationPack as CampaignData["geoImplementationPack"],
      geoResearchPhase: "implementation_plan",
      geoResearchProgress: { phase: "implementation_plan", completed: 6, total: 6 },
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 5,
      stepStatuses: { ...state.stepStatuses, 5: "waiting" },
    }));
    hooks.setFocusedStep(5);
    hooks.addStepLog(5, `[SUCCESS] Implementation plan ready (${taskCount} tasks)`);
    hooks.addMessage({
      role: "agent",
      content: `Implementation plan is ready with ${taskCount} tasks and ${quickWinCount} quick wins. In the Implementation widget, review Execution queue, Ready files, and CMS playbooks.`,
      widget: "geoImplementation",
    });
  });

  sseEvents.subscribe("geo:done", ({ data, resolvedTargetQueries }) => {
    const hooks = getHooks();
    const liveGeoInput = hooks.getGeoInput();
    const updatedGeoInput =
      resolvedTargetQueries && liveGeoInput
        ? { ...liveGeoInput, targetQueries: resolvedTargetQueries }
        : liveGeoInput;
    hooks.updateCampaignData({
      ...(updatedGeoInput ? { geoInput: updatedGeoInput } : {}),
      geoResearchStatus: "complete",
      geoResearchPhase: "done",
      siteScan: (data.siteScan ?? data.site_scan) as CampaignData["siteScan"],
      aiCitations: (data.citations ?? []) as CampaignData["aiCitations"],
      aiShareOfVoice: Number(data.aiShareOfVoice ?? data.ai_share_of_voice ?? 0),
      geoScoreResult: (data.geoScore ?? data.geo_score) as CampaignData["geoScoreResult"],
      contentGaps: (data.contentGaps ?? data.content_gaps ?? []) as CampaignData["contentGaps"],
      geoTopCompetitors: (data.topCompetitors ??
        data.top_competitors ??
        []) as CampaignData["geoTopCompetitors"],
      jsonLdAssets: (data.jsonLdAssets ??
        data.json_ld_assets ??
        []) as CampaignData["jsonLdAssets"],
      copyUpgrades: (data.copyUpgrades ??
        data.copy_upgrades ??
        []) as CampaignData["copyUpgrades"],
      entityPayload: (data.entityPayload ?? data.entity_payload) as CampaignData["entityPayload"],
      geoImplementationPack: (data.implementationPack ??
        data.implementation_pack) as CampaignData["geoImplementationPack"],
      totalCreditsUsed: data.total_credits_used as CampaignData["totalCreditsUsed"],
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 5,
      stepStatuses: { ...state.stepStatuses, 5: "waiting" },
    }));
    hooks.setFocusedStep(5);
    hooks.addStepLog(
      5,
      `[SUCCESS] GEO pipeline complete — total credits used: ${data.total_credits_used ?? 0}`,
    );
    hooks.addMessage({
      role: "agent",
      content:
        "GEO pipeline is complete. Timeline step 5 has the final implementation package and deployment checklist.",
    });
  });

  sseEvents.subscribe("geo:error", ({ message }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({ geoResearchStatus: "error", geoResearchPhase: "error" });
    hooks.addStepLog(1, `[ERROR] ${message}`);
    hooks.setStepState((state) => ({
      currentStepIndex: 0,
      stepStatuses: { ...state.stepStatuses, 0: "waiting", 1: "pending" },
    }));
    hooks.addMessage({
      role: "agent",
      content: `GEO research encountered an error: ${message}`,
    });
  });
}
